import { useEffect, useState } from "react";
import { api } from "@/lib/auth";

// Foundation Membership scarcity — static fallback used before the API
// responds (and when the network is offline).
export const SPOTS_TOTAL = 20;
export const SPOTS_CLAIMED = 14;
export const SPOTS_LEFT = SPOTS_TOTAL - SPOTS_CLAIMED;

const emptyCity = (city, claimed = 0) => ({
  city,
  total: SPOTS_TOTAL,
  claimed,
  left: SPOTS_TOTAL - claimed,
});

const FALLBACK = {
  total: SPOTS_TOTAL,
  claimed: SPOTS_CLAIMED,
  left: SPOTS_LEFT,
  cities: {
    Sydney: emptyCity("Sydney", SPOTS_CLAIMED),
    Melbourne: emptyCity("Melbourne"),
    Brisbane: emptyCity("Brisbane"),
    Perth: emptyCity("Perth"),
  },
};

/**
 * Live Foundation counter. Falls back to the static SPOTS_* values while the
 * request is in flight or if the API is unreachable — the section never
 * renders empty. Returns the full response including per-city stats plus a
 * `pulseKey` that changes whenever the numbers actually shift, so callers
 * can trigger a small "we got a new founder" animation without over-firing.
 */
export function useFoundationStats() {
  const [stats, setStats] = useState(FALLBACK);
  const [pulseKey, setPulseKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/foundation/stats");
        if (cancelled || !data || typeof data.total !== "number") return;
        setStats((prev) => {
          const next = {
            total: data.total,
            claimed: data.claimed,
            left: data.left,
            cities: data.cities || FALLBACK.cities,
          };
          // Only bump pulseKey when Sydney's claimed changes — the visible
          // counter is Sydney-first, no need to animate on Melbourne yet.
          if (prev.claimed !== next.claimed) setPulseKey((k) => k + 1);
          return next;
        });
      } catch {
        /* keep the fallback / previous value */
      }
    };
    fetchOnce();
    // Poll every 30 seconds so the Foundation section reflects new founder
    // signatures without a page refresh. Cheap indexed lookup on the backend.
    timer = setInterval(fetchOnce, 30_000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  return { ...stats, pulseKey };
}
